// src/features/reit-info/components/SafetyMetricsTable.tsx
//
// Component Island (Island 3): leverage & safety metrics table. Crucial for
// checking whether a REIT is over-leveraged in a high-rate environment.
// Sources the SAME cached ratios-ttm payload as the Overview island (the
// backend reuses one FMP call for both), extracting debt-to-equity,
// debt-to-assets, interest coverage, and operating cash flow per share (the
// FFO/share proxy).
//
// Each metric carries a lightweight qualitative badge (Healthy / Moderate /
// Stretched) so a non-specialist can read the leverage picture at a glance.

import { useEffect, useState } from 'react';

import { fetchReitSafetyMetrics, type ReitSafetyMetrics } from '../api';

type Tone = 'good' | 'warn' | 'bad' | 'neutral';

interface Row {
    label: string;
    help: string;
    value: string;
    tone: Tone;
    badge?: string;
}

const toneClasses: Record<Tone, string> = {
    good: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20',
    warn: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20',
    bad: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20',
    neutral: 'text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800',
};

// --- Threshold helpers (typical Real-Estate leverage heuristics) ---------
const gradeDebtToEquity = (v?: number): { tone: Tone; badge: string } => {
    if (v == null) return { tone: 'neutral', badge: '—' };
    if (v <= 1) return { tone: 'good', badge: 'Healthy' };
    if (v <= 2) return { tone: 'warn', badge: 'Moderate' };
    return { tone: 'bad', badge: 'Stretched' };
};

const gradeDebtToAssets = (v?: number): { tone: Tone; badge: string } => {
    if (v == null) return { tone: 'neutral', badge: '—' };
    if (v <= 0.5) return { tone: 'good', badge: 'Healthy' };
    if (v <= 0.65) return { tone: 'warn', badge: 'Moderate' };
    return { tone: 'bad', badge: 'Stretched' };
};

const gradeInterestCoverage = (v?: number): { tone: Tone; badge: string } => {
    if (v == null) return { tone: 'neutral', badge: '—' };
    if (v >= 3) return { tone: 'good', badge: 'Strong' };
    if (v >= 1.5) return { tone: 'warn', badge: 'Adequate' };
    return { tone: 'bad', badge: 'Thin' };
};

// FFO Payout Ratio = Dividends / FFO (FFO = Net Income + Depreciation).
// Arrives as a FRACTION. Above 1.0 (>100%) means the dividend is NOT covered
// by funds from operations — an unhealthy, potentially unsustainable payout.
const gradeFfoPayout = (v?: number): { tone: Tone; badge: string } => {
    if (v == null) return { tone: 'neutral', badge: '—' };
    if (v <= 0.8) return { tone: 'good', badge: 'Healthy' };
    if (v <= 1) return { tone: 'warn', badge: 'Elevated' };
    return { tone: 'bad', badge: 'Unsustainable' };
};

export const SafetyMetricsTable = ({ symbol }: { symbol: string }) => {
    const [metrics, setMetrics] = useState<ReitSafetyMetrics | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError(null);

        fetchReitSafetyMetrics(symbol)
            .then((res) => {
                if (!cancelled) setMetrics(res);
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error(err);
                if (!cancelled) {
                    setError('Safety metrics unavailable.');
                    setMetrics(null);
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [symbol]);

    const fmt = (v?: number, digits = 2) =>
        v != null && Number.isFinite(v) ? v.toFixed(digits) : 'N/A';

    const de = gradeDebtToEquity(metrics?.debt_to_equity);
    const da = gradeDebtToAssets(metrics?.debt_to_assets);
    const ic = gradeInterestCoverage(metrics?.interest_coverage);
    const fp = gradeFfoPayout(metrics?.ffo_payout_ratio);

    const rows: Row[] = [
        {
            label: 'Debt / Equity',
            help: 'Total debt relative to shareholder equity',
            value: fmt(metrics?.debt_to_equity),
            tone: de.tone,
            badge: de.badge,
        },
        {
            label: 'Debt / Assets',
            help: 'Share of assets financed by debt',
            value:
                metrics?.debt_to_assets != null
                    ? `${(metrics.debt_to_assets * 100).toFixed(1)}%`
                    : 'N/A',
            tone: da.tone,
            badge: da.badge,
        },
        {
            label: 'Interest Coverage',
            help: 'Operating income vs. interest expense (×)',
            value: metrics?.interest_coverage != null ? `${fmt(metrics.interest_coverage)}×` : 'N/A',
            tone: ic.tone,
            badge: ic.badge,
        },
        {
            label: 'FFO Payout Ratio',
            help: 'Dividend ÷ FFO (Net Income + Depreciation); >100% is unhealthy',
            value:
                metrics?.ffo_payout_ratio != null
                    ? `${(metrics.ffo_payout_ratio * 100).toFixed(1)}%`
                    : 'N/A',
            tone: fp.tone,
            badge: fp.badge,
        },
        {
            label: 'Op. Cash Flow / Share',
            help: 'FFO-per-share proxy (operating cash flow basis)',
            value: metrics?.ocf_per_share != null ? `$${fmt(metrics.ocf_per_share)}` : 'N/A',
            tone: 'neutral',
        },
    ];

    return (
        <div className="surface-card h-full flex flex-col">
            <h2 className="section-title mb-1">Leverage &amp; Safety</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                Balance-sheet resilience (trailing twelve months)
            </p>

            <div className="flex-1 min-h-[240px]">
                {isLoading ? (
                    <div className="h-full flex items-center justify-center text-slate-500 dark:text-slate-400 animate-pulse text-sm">
                        Loading metrics…
                    </div>
                ) : error ? (
                    <div className="h-full flex items-center justify-center text-sm text-slate-500 dark:text-slate-400">
                        {error}
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100 dark:divide-slate-800/50">
                        {rows.map((r) => (
                            <div
                                key={r.label}
                                className="flex items-center justify-between py-3.5"
                            >
                                <div>
                                    <div className="font-semibold text-slate-900 dark:text-white text-sm">
                                        {r.label}
                                    </div>
                                    <div className="text-xs text-slate-500 dark:text-slate-400">
                                        {r.help}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="font-mono font-bold text-slate-900 dark:text-white">
                                        {r.value}
                                    </span>
                                    {r.badge && r.badge !== '—' && (
                                        <span
                                            className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${toneClasses[r.tone]}`}
                                        >
                                            {r.badge}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SafetyMetricsTable;
